<template>
  <BasePage :page-title="d.title" :show-menu="true" :back-button="true" :back-to="openId ? '/inbox' : '/'" :show-footer="false">
    <div class="container-xl py-3">
      <!-- One notification: from a tapped push (/inbox/:id?delivery=) or the list -->
      <template v-if="openId">
        <div v-if="opening" class="text-center py-4">
          <span class="spinner-border spinner-border-sm"></span>
        </div>
        <div v-else-if="!current" class="alert alert-info">{{ d.notFound }}</div>
        <div v-else class="list-group mb-3">
          <InboxItem :item="current" expanded>
            <template #actions>
              <button v-if="current.canAcknowledge" class="btn btn-warning btn-sm" :disabled="!!inbox.acknowledging"
                      @click="inbox.acknowledge(current.id)">
                <span v-if="inbox.acknowledging === current.id" class="spinner-border spinner-border-sm me-1"></span>
                {{ d.acknowledge }}
              </button>
            </template>
          </InboxItem>
        </div>
        <RouterLink to="/inbox" class="btn btn-outline-secondary btn-sm">{{ d.backToInbox }}</RouterLink>
      </template>

      <template v-else>
        <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
          <p class="text-secondary mb-0 small">{{ d.retention }}</p>
          <button class="btn btn-outline-secondary btn-sm text-nowrap" :disabled="!inbox.unread" @click="inbox.markAllRead()">
            {{ d.markAllRead }}
          </button>
        </div>

        <div v-if="inbox.error && !inbox.items.length" class="alert alert-warning">
          {{ d.loadFailed }}
          <button class="btn btn-sm btn-warning ms-2" @click="inbox.refresh()">{{ d.retry }}</button>
        </div>
        <div v-else-if="!inbox.loaded && inbox.loading" class="text-center py-4">
          <span class="spinner-border spinner-border-sm"></span>
        </div>
        <Card v-else-if="!inbox.items.length" margin="">
          <div class="text-center text-secondary">{{ d.empty }}</div>
        </Card>
        <div v-else class="list-group">
          <InboxItem v-for="n in inbox.items" :key="n.id" :item="n" @open="openFromList" />
        </div>

        <div v-if="inbox.hasMore" class="text-center mt-3">
          <button class="btn btn-outline-secondary btn-sm" :disabled="inbox.loading" @click="inbox.loadMore()">{{ d.loadMore }}</button>
        </div>

        <div class="text-center mt-4">
          <RouterLink to="/notifications" class="small text-secondary">{{ d.notificationSettings }}</RouterLink>
        </div>
      </template>
    </div>
  </BasePage>
</template>

<script setup>
// The staff inbox (#141, notifications-v2): the notifications sent to this
// staff member in the last 30 days, from the server, the same on every device.
// Opening one marks it read; arriving from a tapped push also records
// "opened" on that delivery. One that awaits their acknowledgement has the
// Acknowledge button (#142). With the flag off for the tenant this page sends
// staff to the old messages page.
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BasePage from '@/components/BasePage.vue'
import InboxItem from '@/components/InboxItem.vue'
import { Card } from '@/components/atomic'
import { useInboxStore } from '@/stores/inbox.js'
import { inboxDeepLink } from '@/services/inbox.js'
import { isNotificationsV2Enabled } from '@/services/pushDevice.js'

const d = __DICT__.inbox
const inbox = useInboxStore()
const route = useRoute()
const router = useRouter()

const openId = computed(() => inboxDeepLink(route).notificationId)
const current = ref(null)
const opening = ref(false)

async function show() {
  const { notificationId, deliveryId } = inboxDeepLink(route)
  if (!(await isNotificationsV2Enabled())) {
    await router.replace('/notifications')
    return
  }
  if (!inbox.loaded) inbox.refresh()
  if (!notificationId) {
    current.value = null
    return
  }
  opening.value = true
  try {
    current.value = await inbox.open(notificationId, { deliveryId })
  } finally {
    opening.value = false
  }
  // "Opened" is recorded; a reload or back must not send the delivery again
  if (deliveryId) router.replace({ name: 'Inbox', params: { notificationId } })
}

function openFromList(item) {
  router.push({ name: 'Inbox', params: { notificationId: item.id } })
}

// Keep the open notification in step with the store (a refresh, or an acknowledgement)
watch(() => inbox.items, () => {
  if (current.value) current.value = inbox.items.find(n => n.id === current.value.id) || current.value
})

watch(() => route.fullPath, () => {
  if (route.name === 'Inbox') show()
}, { immediate: true })
</script>
